import { useCallback, useEffect, useRef } from 'react'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  VIEWPOINTS_PANEL_MAX,
  VIEWPOINTS_PANEL_MIN,
} from '@viewer/store/viewpointsSlice'
import ViewpointsPanel from '@viewer/viewpoints'

// Fixed mode: docked between the central canvas and the right sidebar
// (or to the right of everything when the right sidebar is detached).
// Resizable via a left-edge handle. Header surface lets the user pop
// it out as a floating panel or a separate browser window, mirroring
// the right-sidebar UX.
function ViewpointsPanelFixed() {
  const open = useAppStore((s) => s.viewpointsPanelOpen)
  const mode = useAppStore((s) => s.viewpointsPanelMode)
  const width = useAppStore((s) => s.viewpointsPanelWidth)
  const collapsed = useAppStore((s) => s.viewpointsPanelCollapsed)
  const setWidth = useAppStore((s) => s.setViewpointsPanelWidth)
  const setMode = useAppStore((s) => s.setViewpointsPanelMode)
  const setOpen = useAppStore((s) => s.setViewpointsPanelOpen)
  const setCollapsed = useAppStore((s) => s.setViewpointsPanelCollapsed)
  const dragging = useRef(false)

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragging.current = true
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
    },
    [],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return
      // The viewpoints panel sits between the canvas and the right
      // sidebar; it has no fixed left anchor we can read from clientX.
      // Apply movement deltas instead by tracking last clientX.
      // (Simpler: derive from the panel's bounding rect each move.)
      const aside = document.getElementById('vn2r-viewpoints-panel')
      if (!aside) return
      const rect = aside.getBoundingClientRect()
      // Width = right edge - cursor X. The panel is right-anchored
      // to its column in flex; resizing from the left handle expands
      // toward the canvas.
      setWidth(rect.right - e.clientX)
    }
    const onUp = () => {
      dragging.current = false
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [setWidth])

  if (!open || mode !== 'fixed') return null

  // Collapsed: thin vertical strip with an expand chevron, matching
  // the right-sidebar pattern. Sits flush with the Properties strip
  // when both are collapsed.
  if (collapsed) {
    return (
      <aside className="flex w-7 shrink-0 flex-col items-center border-l border-[var(--color-border)] bg-[var(--color-panel)]">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Expandir Viewpoints"
          aria-label="Expandir Viewpoints"
          className="mt-2 flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="mt-3 [writing-mode:vertical-rl] text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
          Viewpoints
        </div>
      </aside>
    )
  }

  return (
    <aside
      id="vn2r-viewpoints-panel"
      className="relative flex shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{
        width: `${width}px`,
        minWidth: `${VIEWPOINTS_PANEL_MIN}px`,
        maxWidth: `${VIEWPOINTS_PANEL_MAX}px`,
      }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onPointerDown}
        title="Arrastar para redimensionar"
        className="absolute -left-[5px] top-0 z-30 h-full w-[10px] cursor-col-resize hover:bg-[var(--color-accent)]/40"
      />
      <header className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs">
        <span className="flex items-center gap-1.5 truncate font-medium text-[var(--color-text)]">
          <ChevronLeft size={12} className="text-[var(--color-text-muted)]" />
          Viewpoints
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode('floating')}
            title="Destacar como janela flutuante"
            className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            ↗ flutuante
          </button>
          <button
            type="button"
            onClick={() => setMode('window')}
            title="Abrir como janela do browser"
            className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            ↗ janela
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Colapsar para o lado direito"
            aria-label="Colapsar"
            className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Fechar"
            aria-label="Fechar"
            className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            ×
          </button>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">
        <ViewpointsPanel />
      </div>
    </aside>
  )
}

export default ViewpointsPanelFixed
